/**
 * PushConsent — OB6: single morning-brief opt-in.
 *
 * One decision: "Want a morning cosmic brief?" Either CTA writes
 * notificationPrefs (master + dailyAdvice) via PATCH /me and finishes onboarding.
 *
 * Mounted at /onboarding/push (onboarding step OB6 → onboardingStep 99).
 *
 * When useDailyBrief() returns real text, we show the actual brief as a
 * preview so the user can make an informed choice ("show, don't tell").
 * When it's loading, show a skeleton. When it errors or text is null,
 * fall back to the abstract greeting screen.
 */

import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../store/auth.js';
import { useFirstName } from '../../hooks/useFirstName.js';
import { useDailyBrief } from '../../hooks/useDailyBrief.js';
import { apiFetch } from '../../lib/api-client.js';
import { requestBotWriteAccess } from '../../lib/telegram.js';
import { CosmosBackdrop } from '../../components/brand/CosmosBackdrop.js';
import { OnboardingProgressBar } from '../../components/onboarding/OnboardingProgressBar.js';
import styles from './PushConsent.module.css';

function BellIcon(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

export function PushConsent(): JSX.Element {
  const { t } = useTranslation('flows');
  const navigate = useNavigate();
  const jwt = useAuth((s) => s.jwt);
  const name = useFirstName();
  const { data, isLoading, isError } = useDailyBrief();
  // Guard against a rapid double-tap firing two PATCH /me requests: the server
  // detects the dailyAdvice false→true flip from a fresh DB read, so two near-
  // simultaneous PATCHes would both see "false" and both emit subscribed_daily.
  // One submit per mount is all this screen ever needs (it navigates away).
  const submittingRef = useRef(false);

  const choose = async (dailyAdvice: boolean): Promise<void> => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    // Direct-link visitors never pressed «Start», so the bot cannot message them
    // yet: ask Telegram right after the «yes» tap. The answer does not change the flow.
    if (dailyAdvice) await requestBotWriteAccess();
    if (jwt) {
      try {
        await apiFetch('/api/v1/me', jwt, {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            notificationPrefs: { master: true, dailyAdvice },
            onboardingStep: 99,
          }),
        });
      } catch {
        // Best-effort — proceed to home even on error.
      }
    }
    navigate('/home', { replace: true });
  };

  const briefText = data?.text ?? null;
  const showBrief = !isError && briefText != null;

  return (
    <div className={styles.root}>
      <CosmosBackdrop />
      <main className={styles.content} role="main" aria-labelledby="push-heading">
        <OnboardingProgressBar step={6} etaSeconds={5} />

        {/* Loading state: skeleton placeholder */}
        {isLoading && !isError && (
          <div className={styles.briefSkeleton} data-testid="push-brief-skeleton" aria-hidden="true" />
        )}

        {/* Brief available: show preview with value-framed CTA */}
        {!isLoading && showBrief && (
          <>
            <header className={styles.header}>
              <h1 id="push-heading" className={styles.title}>
                {t('onboarding.pushConsent.briefHeader')}
              </h1>
            </header>
            <div className={styles.briefCard} data-testid="push-brief">
              <p className={styles.briefText}>{briefText}</p>
            </div>
            <p className={styles.disclaimer}>{t('onboarding.pushConsent.disclaimer')}</p>
            <p className={styles.ask}>{t('onboarding.pushConsent.briefAsk')}</p>
            <div className={styles.ctaArea}>
              <button
                type="button"
                className={styles.ctaAll}
                onClick={() => void choose(true)}
                data-testid="push-cta-yes"
              >
                {t('onboarding.pushConsent.cta.yesDaily')}
              </button>
              <button
                type="button"
                className={styles.ctaMinimal}
                onClick={() => void choose(false)}
                data-testid="push-cta-no"
              >
                {t('onboarding.pushConsent.cta.no')}
              </button>
            </div>
            <p className={styles.disclosure} data-testid="push-disclosure">
              {t('onboarding.pushConsent.disclosure')}
            </p>
          </>
        )}

        {/* Fallback: brief errored, null, or still not loaded — show abstract screen */}
        {!isLoading && !showBrief && (
          <>
            <header className={styles.header}>
              <div className={styles.iconCircle} aria-hidden="true">
                <BellIcon />
              </div>
              <h1 id="push-heading" className={styles.title}>
                {t('onboarding.pushConsent.greeting', { name })}
              </h1>
              <p className={styles.subtitle}>{t('onboarding.pushConsent.body')}</p>
            </header>
            <div className={styles.ctaArea}>
              <button
                type="button"
                className={styles.ctaAll}
                onClick={() => void choose(true)}
                data-testid="push-cta-yes"
              >
                {t('onboarding.pushConsent.cta.yes')}
              </button>
              <button
                type="button"
                className={styles.ctaMinimal}
                onClick={() => void choose(false)}
                data-testid="push-cta-no"
              >
                {t('onboarding.pushConsent.cta.no')}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
